import { PALETTE, BG_PALETTE } from "../lib/theme";
import type { Section5, Section5Idea } from "../lib/types";
import { Podium } from "./podium";

interface Props {
  data: Section5;
}

const RC = [PALETTE[5], PALETTE[1], PALETTE[0]];
const RB = [BG_PALETTE[5], BG_PALETTE[1], BG_PALETTE[0]];
const LABELS = [
  "🥇 Idée 1 — Top recommandation",
  "🥈 Idée 2 — Bonne opportunité",
  "🥉 Idée 3 — À considérer",
];

const NO_COMPETITOR_SENTINEL = "Aucun concurrent direct identifié dans la zone";

// Renders the "concurrent" row. When the backend guardrail (or the LLM)
// has set the sentinel, render a positive "Aucun concurrent direct" pill
// instead of pretending we have a competitor.
function renderCompetitor(idea: Section5Idea, color: string) {
  const isSentinel =
    !idea.concurrent ||
    idea.concurrent.trim() === "" ||
    idea.concurrent === NO_COMPETITOR_SENTINEL;

  if (isSentinel) {
    return (
      <div
        className="rounded-md px-3 py-2.5 text-sm"
        style={{ background: `${color}10`, color }}
      >
        <span className="font-semibold">Aucun concurrent direct</span>{" "}
        identifié dans la zone analysée.
      </div>
    );
  }

  return (
    <div className="text-sm text-slate-600">
      <span className="font-semibold text-slate-700">
        Concurrent le + proche :{" "}
      </span>
      {idea.concurrent}
      {idea.villeConcurrent ? `, ${idea.villeConcurrent}` : ""}
      {idea.distanceConcurrent && (
        <span className="font-semibold" style={{ color }}>
          {" "}— à {idea.distanceConcurrent}
        </span>
      )}
    </div>
  );
}

export function SectionIdeas({ data }: Props) {
  return (
    <div>
      <div className="flex flex-col gap-4">
        {data.idees.map((idea, i) => {
          const c = RC[i % 3];
          const bg = RB[i % 3];
          const activityNode = idea.enseigne_slug ? (
            <a
              href={`/enseignes/${idea.enseigne_slug}`}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {idea.activite}
            </a>
          ) : (
            idea.activite
          );

          return (
            <div
              key={`idea-${i}`}
              className="overflow-hidden rounded-2xl bg-white"
              style={{ border: `1.5px solid ${c}44` }}
            >
              <div
                className="px-3.5 py-2.5"
                style={{
                  background: bg,
                  borderBottom: `1px solid ${c}22`,
                }}
              >
                <div
                  className="text-[11px] font-bold uppercase tracking-wide"
                  style={{ color: c }}
                >
                  {LABELS[i] ?? `Idée ${i + 1}`}
                </div>
                <div className="mt-1 text-lg font-bold text-slate-900">
                  {activityNode}
                </div>
              </div>
              <div className="p-3.5">
                <div className="mb-3 flex flex-col gap-2">
                  {renderCompetitor(idea, c)}
                  <div className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-700">
                      Surface moyenne :{" "}
                    </span>
                    {idea.surfaceMoyenne}
                  </div>
                  <div className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-700">
                      Investissement :{" "}
                    </span>
                    {idea.investissementMoyen}
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-sm text-slate-500">
                    <span>Score de pertinence</span>
                    <span className="font-semibold" style={{ color: c }}>
                      {idea.score}/100
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, Math.max(0, idea.score))}%`,
                        background: c,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Podium idees={data.idees} />
    </div>
  );
}
